import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Trash2, Loader2, AlertTriangle } from 'lucide-react';
import { httpsCallable } from '@/lib/callableProxy';
import { functions as firebaseFunctions } from '@/config/firebase';
import { toast as sonnerToast } from "sonner";
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

interface DeleteWorkspaceCardProps {
    workspaceId: string;
    workspaceName: string;
}

export const DeleteWorkspaceCard: React.FC<DeleteWorkspaceCardProps> = ({ workspaceId, workspaceName }) => {
    const { fetchUserSettings } = useAuth();
    const navigate = useNavigate();
    const [isDeleting, setIsDeleting] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [confirmName, setConfirmName] = useState('');

    const handleDelete = async () => {
        if (confirmName.trim().toLowerCase() !== workspaceName.trim().toLowerCase()) return;

        setIsDeleting(true);
        const toastId = sonnerToast.loading("Deleting workspace...");
        
        try {
            const deleteFn = httpsCallable(firebaseFunctions, 'deleteWorkspace');
            const result = await deleteFn({ workspaceId }) as any;
            
            sonnerToast.success("Workspace deleted.", { id: toastId });
            
            await fetchUserSettings(); 
            setIsOpen(false);
            setConfirmName('');

            if (!result.data.nextWorkspaceId) {
                navigate('/studio'); 
            }
            
        } catch (error: any) {
            sonnerToast.error(`Delete failed: ${error.message}`, { id: toastId });
            setIsDeleting(false);
        }
    };

    const isMatch = confirmName.trim().toLowerCase() === workspaceName.trim().toLowerCase();

    return (
        <Card className="border-destructive/50 bg-destructive/5">
            <CardHeader>
                <CardTitle className="flex items-center gap-2 text-destructive tracking-[-0.02em]">
                    <AlertTriangle size={20}/> Danger Zone
                </CardTitle>
                <CardDescription className="text-destructive/80">
                    Permanently delete this workspace and all its data. This action cannot be undone.
                </CardDescription>
            </CardHeader>
            <CardContent>
                <AlertDialog open={isOpen} onOpenChange={(open) => { setIsOpen(open); if (!open) setConfirmName(''); }}>
                    <AlertDialogTrigger asChild>
                        <Button variant="destructive" className="rounded-xl" disabled={isDeleting}>
                            {isDeleting ? <Loader2 className="mr-2 h-4 w-4 animate-spin"/> : <Trash2 className="mr-2 h-4 w-4"/>}
                            Delete Workspace
                        </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="rounded-2xl border-slate-200 bg-white">
                        <AlertDialogHeader>
                            <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                            <AlertDialogDescription className="space-y-4">
                                <p>
                                    This action cannot be undone. This will permanently delete the workspace 
                                    <span className="font-bold text-foreground mx-1">{workspaceName}</span> 
                                    and remove all associated data, invoices, and member access.
                                </p>
                                <div className="space-y-2">
                                    <Label htmlFor="confirm-name" className="text-foreground">
                                        Type <span className="font-mono font-bold text-destructive select-all">{workspaceName}</span> to confirm.
                                    </Label>
                                    <Input 
                                        id="confirm-name" 
                                        value={confirmName} 
                                        onChange={(e) => setConfirmName(e.target.value)}
                                        placeholder={workspaceName}
                                        className="rounded-xl border-destructive/50 bg-white focus-visible:ring-destructive"
                                        autoComplete="off"
                                    />
                                </div>
                            </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                            <AlertDialogCancel className="rounded-xl border-slate-200 bg-white hover:bg-slate-50" disabled={isDeleting}>Cancel</AlertDialogCancel>
                            <AlertDialogAction 
                                onClick={(e) => { e.preventDefault(); handleDelete(); }} 
                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90" 
                                disabled={isDeleting || !isMatch}
                            >
                                {isDeleting && <Loader2 className="mr-2 h-4 w-4 animate-spin"/>}
                                I understand, delete this workspace
                            </AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>
            </CardContent>
        </Card>
    );
};
